import { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar/AppBar';
import { CSSObject, styled, Theme } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import {
    ACCOUNTS,
    CASES_GENERAL,
    CASES_GENERAL_INFORMATION,
    HOME,
    PQR,
    REDIRECTLIST,
    SARLAFT,
    LEGAL_PROCEEDINGS,
    USERLIST,
    ALERTS,
    SAMPLES,
    PQR_HISTORY,
    INITIAL_CAUSALITY,
    FINAL_CAUSALITY,
    MOTIVE,
} from '../../../routes/paths';
import { ModulesMessages } from '../../../shared/messages/modulesMessages';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import AppsIcon from '@mui/icons-material/Apps';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PqrIcon from '@mui/icons-material/FlightClass';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import EscalatorWarningIcon from '@mui/icons-material/EscalatorWarning';
import NotesIcon from '@mui/icons-material/Notes';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import GavelIcon from '@mui/icons-material/Gavel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InboxIcon from '@mui/icons-material/Inbox';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import SpaIcon from '@mui/icons-material/Spa';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import ChecklistIcon from '@mui/icons-material/Checklist';

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

export const drawerWidth = 300;

export const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    height: '100%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
});

export const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    height: '100%',
    width: `calc(${theme.spacing(9)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
        width: `calc(${theme.spacing(10)} + 1px)`,
    },
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
});

export const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));

export const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    borderColor: theme.palette.primary.contrastText,
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

export const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    borderColor: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        borderColor: theme.palette.primary.contrastText,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));

const {
    HOME_MODULE,
    MODULES_MODULE,
    ACCOUNTS_PAGE,
    PQR_PAGE,
    PQR_HISTORY_PAGE,
    GENERAL_PAGE,
    SARLAFT_PAGE,
    REDIRECT_PAGE,
    USERS_MODULE,
    CASES_PAGE,
    MODULES_PQR_MANAGEMENT,
    LEGAL_PROCEEDING,
    ALERTS_MODULE,
    SAMPLES_MODULE,
    MODULES_TYPIFICATIONS,
    FINAL_CAUSALITY_MODULE,
    INITIAL_CAUSALITY_PAGE,
    SYSTEM_CONFIGURATION_MODULE,
    MOTIVE_PAGE,
} = ModulesMessages;

export const ModulesSideBar = [
    {
        route: HOME,
        label: HOME_MODULE,
        icon: <HomeRoundedIcon sx={{ color: 'primary.contrastText' }} />,
    },
    {
        label: MODULES_MODULE,
        icon: <AppsIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
        ],
        subItems: [
            {
                route: ACCOUNTS,
                label: ACCOUNTS_PAGE,
                icon: <AccountBoxIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: CASES_GENERAL_INFORMATION,
                label: GENERAL_PAGE,
                icon: (
                    <FormatListBulletedIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: PQR,
                label: PQR_PAGE,
                icon: <PqrIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: PQR_HISTORY,
                label: PQR_HISTORY_PAGE,
                icon: (
                    <ManageSearchIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: SARLAFT,
                label: SARLAFT_PAGE,
                icon: (
                    <EscalatorWarningIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: CASES_GENERAL,
                label: CASES_PAGE,
                icon: <NotesIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
        ],
    },
    // {
    //     route: EXPORT,
    //     label: EXPORT_MODULE,
    //     icon: <DownloadIcon sx={{ color: 'primary.contrastText' }} />,
    //     permissions: ['admin'],
    // },
    {
        label: MODULES_PQR_MANAGEMENT,
        icon: <HeadsetMicIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
        ],
        subItems: [
            {
                route: LEGAL_PROCEEDINGS,
                label: LEGAL_PROCEEDING,
                icon: <GavelIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: ALERTS,
                label: ALERTS_MODULE,
                icon: (
                    <NotificationsActiveIcon
                        sx={{ color: 'primary.contrastText' }}
                    />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: SAMPLES,
                label: SAMPLES_MODULE,
                icon: <InboxIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
        ],
    },
    {
        label: MODULES_TYPIFICATIONS,
        icon: <CategoryIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: [
            'admin',
            'agent',
            'backoffice',
            'supervisor',
            'formador',
            'calidad',
        ],
        subItems: [
            {
                route: INITIAL_CAUSALITY,
                label: INITIAL_CAUSALITY_PAGE,
                icon: <SpaIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
            {
                route: FINAL_CAUSALITY,
                label: FINAL_CAUSALITY_MODULE,
                icon: (
                    <CheckCircleIcon sx={{ color: 'primary.contrastText' }} />
                ),
                permissions: [
                    'admin',
                    'agent',
                    'backoffice',
                    'supervisor',
                    'formador',
                    'calidad',
                ],
            },
        ],
    },
    {
        label: SYSTEM_CONFIGURATION_MODULE,
        icon: <SettingsIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: ['admin', 'supervisor', 'formador', 'calidad'],
        subItems: [
            {
                route: REDIRECTLIST,
                label: REDIRECT_PAGE,
                icon: <SpaIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin', 'supervisor', 'formador', 'calidad'],
            },
            {
                route: MOTIVE,
                label: MOTIVE_PAGE,
                icon: <ChecklistIcon sx={{ color: 'primary.contrastText' }} />,
                permissions: ['admin'],
            },
        ],
    },
    {
        route: USERLIST,
        label: USERS_MODULE,
        icon: <PeopleAltRoundedIcon sx={{ color: 'primary.contrastText' }} />,
        permissions: ['admin', 'supervisor'],
    },
];
